'use client'

import React, { useState, useEffect } from 'react'
import {
  Typography,
  IconButton,
  Snackbar,
  Alert
} from '@mui/material'
import { ShoppingCart as ShoppingCartIcon } from 'lucide-react'
import '../../../sass/menu/OrderingWidget.scss'
import { useCart } from '@/contexts/CartContext'
import { Language } from '@/lib/interfaces/interfaces'
import { IOrderPayload, IShippingDetails } from '@/lib/interfaces/ordering'
import axios from 'axios'
import CartList from './CartList'
import ActionButtons from './ActionButtons'
import ShippingDetails from './ShippingDetails'

interface Props {
  slug: string
  language: Language
  barId: string
  deliveryFee: number
  minOrder: number
}

// Objeto de traducciones con textos para cada idioma
const translations = {
  es: {
    requiredFirstName: 'El nombre es requerido',
    requiredLastName: 'El apellido es requerido',
    requiredAddress: 'La dirección es requerida',
    requiredCity: 'La ciudad es requerida',
    requiredPostalCode: 'El código postal es requerido',
    requiredProvince: 'La provincia es requerida',
    invalidPhone: 'El teléfono no es correcto',
    product: 'producto',
    products: 'productos',
    openCart: 'Abrir carrito',
    minOrderError: 'El pedido mínimo es de',
    deliveryZoneError: 'Lo sentimos, no entregamos en tu zona',
    orderProcessError: 'Error al procesar el pedido'
  },
  en: {
    requiredFirstName: 'First name is required',
    requiredLastName: 'Last name is required',
    requiredAddress: 'Address is required',
    requiredCity: 'City is required',
    requiredPostalCode: 'Postal code is required',
    requiredProvince: 'Province is required',
    invalidPhone: 'Phone number is invalid',
    product: 'product',
    products: 'products',
    openCart: 'Open cart',
    minOrderError: 'Minimum order amount is',
    deliveryZoneError: 'Sorry, we do not deliver to your area',
    orderProcessError: 'Error processing the order'
  },
  fr: {
    requiredFirstName: 'Le prénom est requis',
    requiredLastName: 'Le nom est requis',
    requiredAddress: "L'adresse est requise",
    requiredCity: 'La ville est requise',
    requiredPostalCode: 'Le code postal est requis',
    requiredProvince: 'La province est requise',
    invalidPhone: 'Le téléphone n’est pas correct',
    product: 'produit',
    products: 'produits',
    openCart: 'Ouvrir le panier',
    minOrderError: 'Le montant minimum de commande est de',
    deliveryZoneError: 'Désolé, nous ne livrons pas dans votre zone',
    orderProcessError: 'Erreur lors du traitement de la commande'
  }
}

const OrderingCart = ({ language, slug, barId, deliveryFee, minOrder }: Props) => {
  const { cart, removeFromCart, clearCart, updateQuantity } = useCart()
  const [isOpen, setIsOpen] = useState(false)
  const [whatToShow, setWhatToShow] = useState<'cartList' | 'shippingDetails'>('cartList')
  const [loading, setLoading] = useState(false)
  const tableId = 'delivery' // Cambiar por el parámetro de la URL si es para pedido en mesa

  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth)

  const [shippingDetails, setShippingDetails] = useState<IShippingDetails>({
    firstName: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    postalCode: '',
    province: '',
    phone: '',
    additionalInfo: ''
  })

  const [shippingErrors, setShippingErrors] = useState<{ [key: string]: string }>({})

  const [errorMessage, setErrorMessage] = useState<string>('')
  const [openSnackbar, setOpenSnackbar] = useState(false)

  // Actualizar el ancho de la ventana al redimensionar
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Deshabilitar el scroll del body cuando el carrito está abierto
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'auto'
  }, [isOpen])

  // Calcular el total del carrito y la cantidad total de productos
  const total: number = cart.reduce(
    (acc, item) => acc + item.variant.price * item.quantity,
    0
  )
  const totalItems = cart.reduce((acc, item) => acc + item.quantity, 0)

  // Manejar el cambio de cantidad
  const handleQuantityChange = (
    itemId: string,
    variantType: string,
    quantity: number
  ) => {
    if (quantity < 1) return
    updateQuantity(itemId, variantType, quantity)
  }

  // Alternar la vista del carrito
  const toggleCart = () => {
    setIsOpen(!isOpen)
  }

  const validateShippingDetails = (): { [key: string]: string } => {
    const errors: { [key: string]: string } = {}
    const texts = translations[language] || translations.es

    if (!shippingDetails.firstName.trim()) errors.firstName = texts.requiredFirstName
    if (!shippingDetails.lastName.trim()) errors.lastName = texts.requiredLastName
    if (!shippingDetails.addressLine1.trim()) errors.addressLine1 = texts.requiredAddress
    if (!shippingDetails.city.trim()) errors.city = texts.requiredCity
    if (!shippingDetails.postalCode.trim()) errors.postalCode = texts.requiredPostalCode
    if (!shippingDetails.province.trim()) errors.province = texts.requiredProvince
    if (
      shippingDetails.phone.trim().length < 7 ||
      !/^\+?\d+$/.test(shippingDetails.phone.trim())
    ) { errors.phone = texts.invalidPhone }

    return errors
  }

  const handleSubmitOrder = async () => {
    if (cart.length === 0) return

    try {
      setLoading(true)
      const orderPayload: IOrderPayload = {
        slug,
        language,
        order: cart,
        tableId,
        barId,
        shippingDetails
      }
      const { data } = await axios.post('/api/ordering/checkout', orderPayload)

      // Redirect to Stripe checkout
      window.location.href = data.checkoutUrl
    } catch (error: any) {
      const texts = translations[language] || translations.es
      let errorMsg = texts.orderProcessError

      // Handle specific error messages
      const serverMessage = error.response?.data?.message
      if (serverMessage) {
        if (serverMessage.includes('MIN_ORDER_ERROR')) {
          errorMsg = `${texts.minOrderError} ${minOrder}€`
        } else if (serverMessage.includes('DELIVERY_ZONE_ERROR')) {
          errorMsg = texts.deliveryZoneError
        }
      }

      setErrorMessage(errorMsg)
      setOpenSnackbar(true)
    } finally {
      setLoading(false)
    }
  }

  const handleMainBtnClick = () => {
    if (whatToShow === 'cartList') {
      setWhatToShow('shippingDetails')
    } else {
      const errors = validateShippingDetails()
      if (Object.keys(errors).length > 0) {
        setShippingErrors(errors)
        return
      }
      handleSubmitOrder()
    }
  }

  const handleSecondaryBtnClick = () => {
    if (whatToShow === 'cartList') {
      clearCart()
      setIsOpen(false)
    } else if (whatToShow === 'shippingDetails') {
      setWhatToShow('cartList')
    }
  }

  const texts = translations[language] || translations.es

  return (
    <>
      {/* Overlay solo se muestra cuando el carrito está abierto */}
      {isOpen && (
        <div
          className='orderingCart__overlay'
          onClick={toggleCart}
        />
      )}

      {/* Vista completa del carrito */}
      <div
        className={`orderingCart__container ${
          isOpen
            ? windowWidth >= 960
              ? 'orderingCart__container--open'
              : 'orderingCart__container--open'
            : windowWidth >= 960
            ? 'orderingCart__container--closed'
            : 'orderingCart__container--closed'
        }`}
      >
        {whatToShow === 'cartList'
          ? (
            <CartList
              toggleCart={toggleCart}
              cart={cart}
              removeFromCart={removeFromCart}
              handleQuantityChange={handleQuantityChange}
              language={language}
            />
            )
          : (
            <ShippingDetails
              shippingDetails={shippingDetails}
              setShippingDetails={setShippingDetails}
              toggleCart={toggleCart}
              errors={shippingErrors}
              language={language}
            />
            )}

        {/* Total y botones de acción fijados al fondo */}
        <ActionButtons
          total={total}
          whatToShow={whatToShow}
          handleSubmitClick={handleMainBtnClick}
          handleSecondaryBtnClick={handleSecondaryBtnClick}
          nItems={totalItems}
          deliveryFee={deliveryFee}
          minOrder={minOrder}
          loading={loading}
          language={language}
        />
      </div>

      {/* Barra minimizada del carrito */}
      {!isOpen && (
        <div
          className='orderingCart__minimized'
          onClick={toggleCart}
        >
          {/* Icono de cesta */}
          <IconButton
            className='minimized__icon'
            aria-label={texts.openCart}
          >
            <ShoppingCartIcon size={20} />
          </IconButton>

          {/* Información de productos */}
          <div className='minimized__info'>
            <Typography variant='subtitle1'>
              {totalItems} {totalItems === 1 ? texts.product : texts.products}
            </Typography>
          </div>

          {/* Precio */}
          <div className='minimized__price'>
            <Typography variant='subtitle1'>
              {total.toFixed(2)}€
            </Typography>
          </div>
        </div>
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity='error'
          variant='filled'
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  )
}

export default OrderingCart
